"use client";

import React from "react";
import Image from "next/image";
import { GoArrowUpRight } from "react-icons/go";
import CustomButton from "src/components/atoms/CustomButton";
import HeroFeaturedCard from "src/components/molecules/HeroFeaturedCard";

const AppHero = ({ heroData }) => {
  const { backgroundImage, subTitle, title, description, primaryButton, secondaryButton, featureData, whiteImage } = heroData || {};

  const isFlutter = backgroundImage?.includes("Flutter") || backgroundImage?.includes("banner");

  const imageAspect = isFlutter ? 2.13 : 1.77;

  return (
    <>
      <div className="bg-background-primary min-h-[85vh] pt-0! pb-10! lg:pt-5! lg:pb-10! relative overflow-hidden flex flex-col lg:flex-row lg:items-center">
        {/* One <picture>-style approach: still 2 <Image> max (mobile + desktop), not 4 */}
        {whiteImage && (
          <div
            className="relative w-full h-[260px] sm:h-[340px] lg:h-full lg:absolute lg:top-0 lg:right-0 z-0 dark:hidden hero-bg-container"
            style={{ "--image-aspect": imageAspect }}
          >
            <Image
              src={whiteImage}
              alt="Hero Background"
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 1200px"
              className="object-cover lg:object-contain object-top-right"
              quality={75}
            />
          </div>
        )}
        {backgroundImage && (
          <div
            className="relative w-full h-[260px] sm:h-[340px] lg:h-full lg:absolute lg:top-0 lg:right-0 z-0 hidden dark:block hero-bg-container"
            style={{ "--image-aspect": imageAspect }}
          >
            <Image
              src={backgroundImage}
              alt="Hero Background"
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 1200px"
              className="object-cover lg:object-contain object-top-right"
              quality={75}
            />
          </div>
        )}

        <div
          className="hidden lg:block absolute inset-0 z-10 pointer-events-none"
          style={{
            background: "var(--hero-overlay-gradient)",
          }}
        />

        <div className="relative z-20 w-full flex flex-col justify-center px-6! sm:px-12! lg:px-10! py-12! lg:py-0! text-text-primary hero-content">
          <div className="w-full lg:w-[60%] xl:w-[45%] 2xl:w-[50%] max-w-full lg:max-w-[500px] xl:max-w-[550px] 2xl:max-w-[750px]">
            <div>
              <div className="flex items-center gap-4 mb-4!">
                <h3 className="hero-subtitle text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
                  {subTitle}
                </h3>

                <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
              </div>

              <h1 className="hero-title text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6! text-text-primary">{title}</h1>

              <p className="hero-description text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg leading-relaxed">{description}</p>
            </div>

            <div className="my-8! flex justify-start gap-3 flex-col md:flex-row">
              {primaryButton && <CustomButton title="Contact Us" href="/contact-us" variant="primary" icon={<GoArrowUpRight />} />}

              {secondaryButton && <CustomButton title="view our work" href="/success-stories" variant="outline" icon={<GoArrowUpRight />} />}
            </div>

            {featureData && (
              <div className="grid grid-cols-1 md:grid-cols-4   bg-transparent rounded-2xl overflow-hidden gap-y-4 md:gap-y-0 lg:gap-y-4 xl:gap-y-4 2xl:gap-y-0 pb-6!">
                {featureData?.map((feature, index) => (
                  <HeroFeaturedCard key={index} feature={feature} isLast={index === featureData?.length - 1} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @media screen and (min-width: 1024px) and (max-width: 1366px) {
          .hero-bg-container {
            right: -250px !important;
            opacity: 0.8 !important;
          }

          .hero-content {
            padding-left: 3rem !important;
            padding-right: 3rem !important;
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
            margin: 0 !important;
          }
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-bg-container {
            right: -300px !important;
            width: 85% !important;
          }

          .hero-content {
            padding-left: 3rem !important;
            padding-right: 3rem !important;
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
            margin: 0 !important;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-bg-container {
            right: 0px !important;
            width: 80% !important;
          }

          .hero-content {
            padding-left: 2.5rem !important;
            padding-right: 2.5rem !important;
            padding-top: 2rem !important;
            padding-bottom: 2rem !important;
            margin: 0 !important;
          }
          .hero-title {
            font-size: 3rem !important;
            line-height: 1.2 !important;
            margin-bottom: 1rem !important;
          }

          .hero-description {
            font-size: 16px !important;
            line-height: 1.5 !important;
          }

          .hero-subtitle {
            font-size: 1.1rem !important;
          }
        }
      `}</style>
    </>
  );
};

export default AppHero;
